import type { Column } from "@tanstack/react-table";
import type React from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import type { DispatchTableRow } from "@/hooks/dispatch/dispatchTableRow";
import DataTable, { type DataTableGroup } from "@/ui/DataTable";
import type { DataTableFeatures, DataTableInstance } from "@/ui/dataTableFeatures";
import { brandPrimary, zinc } from "@/ui/tokens";
import { DISPATCH_DETAIL_ESTIMATED_HEIGHT } from "./DispatchRowDetail";
import { DISPATCH_HIGHLIGHT, FILTERED_TINT } from "./dispatchColors";

/** Row height in px: dense enough to scan a shift's traffic without paging, tall enough to be easy on the eyes. */
export const DISPATCH_ROW_HEIGHT = 40;

/** Header height in px; the header stays tighter than the rows. */
export const DISPATCH_HEADER_HEIGHT = 32;

/**
 * How many whole rows a table this tall shows under its sticky header: the screenful the page keys move by.
 *
 * At least one, so a squeezed table still pages. The unfolded detail and group headers take space too, so a page can
 * land a little past the screen; scrolling the highlight into view covers it.
 *
 * @param viewportHeight - The table's height in px, header included.
 */
export function dispatchPageRowCount(viewportHeight: number): number {
  return Math.max(1, Math.floor((viewportHeight - DISPATCH_HEADER_HEIGHT) / DISPATCH_ROW_HEIGHT));
}

/**
 * Solid row tints (frozen cells must be opaque), each the table's base lifted toward its meaning. The keyboard's row
 * takes `DISPATCH_HIGHLIGHT`, which the detail beneath it shares.
 */
const ROW_TINT = {
  /** A ticked row (checkbox): a quiet blue, apart from the keyboard's teal. */
  checked: "#2a3247",
  /** The row whose channel is open in the popover. */
  open: zinc[600],
} as const;

/** Matches the selected row while the inline reply in the detail beneath it has the cursor. */
const REPLYING_ROW = ".dispatch-row-selected:has(+ .datatable-row-detail textarea:focus)";

/**
 * The virtualized, dark-themed Dispatch grid over a table built by `DispatchView`. Selection, ticked rows, and the open
 * driver are painted by class so the highlight survives virtualization (the acknowledgement dot is a cell of
 * its own); pinned columns paint their own
 * background so they never show the rows scrolling behind them. The selected row unfolds a detail row beneath it; group
 * headers, when the view groups, fold and unfold on click.
 */
export default function DispatchTable({
  table,
  onRowClick,
  onRowContextMenu,
  onRowDoubleClick,
  onHeaderClick,
  getRowClassName,
  scrollToRowId,
  expandedRowId,
  renderExpandedRow,
  columnPinning,
  onColumnPinningChange,
  groups,
  onToggleGroup,
  noRowsContent,
  filteredColumnIds,
}: {
  table: DataTableInstance<DispatchTableRow>;
  onRowClick?: (row: DispatchTableRow) => void;
  onRowContextMenu?: (row: DispatchTableRow, event: React.MouseEvent) => void;
  onRowDoubleClick?: (row: DispatchTableRow, event: React.MouseEvent) => void;
  onHeaderClick?: (column: Column<DataTableFeatures, DispatchTableRow>, event: React.MouseEvent<HTMLElement>) => void;
  getRowClassName?: (row: DispatchTableRow) => string;
  scrollToRowId?: string | null;
  expandedRowId?: string | null;
  renderExpandedRow?: (row: DispatchTableRow) => React.ReactNode;
  columnPinning?: { left: string[] };
  onColumnPinningChange?: (left: string[]) => void;
  groups?: readonly DataTableGroup[];
  onToggleGroup?: (key: string) => void;
  noRowsContent?: React.ReactNode;
  /** The columns an ad-hoc condition narrows on; their header cells are highlighted edge to edge. */
  filteredColumnIds?: ReadonlySet<string>;
}) {
  const { selected, composing } = DISPATCH_HIGHLIGHT;
  const filteredHeaderSelector = [...(filteredColumnIds ?? [])]
    .map((id) => `& .MuiTableHead-root .MuiTableCell-root[data-col-id=${JSON.stringify(id)}]`)
    .join(", ");
  return (
    <DataTable
      table={table}
      onRowClick={onRowClick}
      onRowContextMenu={onRowContextMenu}
      onRowDoubleClick={onRowDoubleClick}
      onHeaderClick={onHeaderClick}
      getRowClassName={getRowClassName}
      scrollToRowId={scrollToRowId}
      expandedRowId={expandedRowId}
      renderExpandedRow={renderExpandedRow}
      expandedRowHeight={DISPATCH_DETAIL_ESTIMATED_HEIGHT}
      columnPinning={columnPinning}
      onColumnPinningChange={onColumnPinningChange}
      rowTestId={TestIds.dispatch.row}
      containerTestId={TestIds.dispatch.table}
      groups={groups}
      onToggleGroup={onToggleGroup}
      groupTestId={TestIds.dispatch.group}
      noRowsContent={noRowsContent ?? "No drivers"}
      pagination={false}
      virtualize
      enableColumnHiding
      rowHeight={DISPATCH_ROW_HEIGHT}
      headerHeight={DISPATCH_HEADER_HEIGHT}
      sx={{
        // Rows are for selecting, not for selecting text: a click or drag never leaves a text anchor for Shift with
        // an arrow to grow, so the keys always work the row selection. The unfolded detail keeps its receipt copyable.
        userSelect: "none",
        "& .datatable-row-detail": { userSelect: "text" },
        "& .MuiTableContainer-root": { border: "none" },
        "& .MuiTableCell-root": {
          py: 0.25,
          px: 1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          borderRight: `1px solid rgba(228, 228, 231, 0.12)`,
        },
        "& .MuiTableHead-root .MuiTableCell-root": { bgcolor: zinc[800] },
        // A filtered column's header cell: the brand tint over the opaque header, so rows never show through the
        // sticky cell, and a brand outline on the cell's own edges, which takes no width from the column.
        ...(filteredHeaderSelector
          ? {
              [filteredHeaderSelector]: {
                backgroundImage: `linear-gradient(${FILTERED_TINT}, ${FILTERED_TINT})`,
                color: brandPrimary[300],
                outline: `1px solid ${brandPrimary[400]}`,
                outlineOffset: -1,
              },
            }
          : {}),
        // The structural columns (the acknowledgement dot, the selection box) carry no separator.
        "& .MuiTableCell-root[data-col-id='_dot'], & .MuiTableCell-root[data-col-id='_select']": {
          borderRight: "none",
          px: 0.5,
        },
        // Every row reads alike: the table paints no read state.
        "& .MuiTableBody-root .MuiTableCell-root": { color: zinc[300] },
        "& .MuiTableBody-root .frozen-column": { bgcolor: zinc[800] },
        "& .MuiTableRow-root:hover:not(.dispatch-row-open) .frozen-column": { bgcolor: zinc[700] },
        // A ticked row: the checkbox's own tint, under the keyboard's and the open row's.
        "& .dispatch-row-checked .MuiTableCell-root": { bgcolor: ROW_TINT.checked },
        "& .dispatch-row-checked .frozen-column": { bgcolor: ROW_TINT.checked },
        // Group headers read as dividers: darker than the rows, no cell borders, the count in the muted color.
        "& .datatable-group-row .MuiTableCell-root": {
          bgcolor: zinc[900],
          color: zinc[200],
          borderRight: "none",
          fontSize: "0.75rem",
        },
        "& .datatable-group-row:hover .MuiTableCell-root": { bgcolor: zinc[800] },
        "& .dispatch-row-open .MuiTableCell-root": { bgcolor: ROW_TINT.open },
        "& .dispatch-row-open .frozen-column": { bgcolor: ROW_TINT.open },
        // The keyboard's row and the detail beneath it read as one block, in a teal that stands apart from every
        // other tint, with one bar down the left edge that the detail continues (a `tr` border does not paint under
        // `border-collapse: separate`, so it is an inset on the first cell) and no divider between the two.
        "& .dispatch-row-selected .MuiTableCell-root": { bgcolor: selected.tint, borderBottom: "none" },
        "& .dispatch-row-selected .frozen-column": { bgcolor: selected.tint },
        "& .MuiTableRow-root.dispatch-row-selected:hover .MuiTableCell-root, & .MuiTableRow-root.dispatch-row-selected:hover .frozen-column":
          { bgcolor: selected.hover },
        "& .dispatch-row-selected .MuiTableCell-root:first-of-type": { boxShadow: `inset 3px 0 0 ${selected.bar}` },
        // While the reply beneath the row has the cursor, the row turns purple with the detail (which paints its own
        // half). The detail's `tr` directly follows the selected row's, so the row reads the focus through `:has()`;
        // outranking the hover rules above, the purple holds under the pointer.
        [`& ${REPLYING_ROW} .MuiTableCell-root, & ${REPLYING_ROW} .frozen-column`]: { bgcolor: composing.tint },
        [`& .MuiTableRow-root${REPLYING_ROW}:hover .MuiTableCell-root, & .MuiTableRow-root${REPLYING_ROW}:hover .frozen-column`]:
          { bgcolor: composing.hover },
        [`& ${REPLYING_ROW} .MuiTableCell-root:first-of-type`]: { boxShadow: `inset 3px 0 0 ${composing.bar}` },
        // The detail's content sticks to the left edge, which a clipping cell would stop. The cell carries the detail's
        // tint too, under the width of a wide table that the content, held to the screen, does not cover.
        "& .datatable-row-detail .MuiTableCell-root": {
          p: 0,
          borderRight: "none",
          whiteSpace: "normal",
          overflow: "visible",
          bgcolor: selected.tint,
        },
        "& .datatable-row-detail:has(textarea:focus) .MuiTableCell-root": { bgcolor: composing.tint },
      }}
    />
  );
}
